import type { FC } from 'react';
import {
    Box,
    Button,
    Drawer,
    Stack,
    Typography,
    IconButton,
    Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useState } from 'react';
import { ThemeToggle } from 'src/components/theme-toggle';

interface MobileNavProps {
    items: {
        title: string;
        path?: string;
        external?: boolean;
        popover?: React.ReactNode;
    }[];
    onClose?: () => void;
    open?: boolean;
}

export const MobileNav: FC<MobileNavProps> = (props) => {
    const { items, onClose, open = false } = props;
    const [useCasesOpen, setUseCasesOpen] = useState(false);

    return (
        <Drawer
            anchor="right"
            onClose={onClose}
            open={open}
            PaperProps={{
                sx: {
                    maxWidth: '100%',
                    width: 300
                }
            }}
            variant="temporary"
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        borderBottomColor: 'divider',
                        borderBottomStyle: 'solid',
                        borderBottomWidth: 1,
                        display: 'flex',
                        flexShrink: 0,
                        p: 2
                    }}
                >
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        p: 2
                    }}
                >
                    <Stack spacing={2}>
                        <ThemeToggle />
                        {items.map((item) => {
                            // Handle Use Cases separately
                            if (item.title === 'Use Cases') {
                                return (
                                    <Box key={item.title}>
                                        <Button
                                            onClick={() => setUseCasesOpen(!useCasesOpen)}
                                            sx={{
                                                justifyContent: 'space-between',
                                                px: 2,
                                                py: 1,
                                                textAlign: 'left',
                                                width: '100%',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover'
                                                }
                                            }}
                                        >
                                            <Typography variant="subtitle2">
                                                {item.title}
                                            </Typography>
                                            {useCasesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </Button>
                                        <Collapse in={useCasesOpen}>
                                            <Stack
                                                spacing={1}
                                                sx={{
                                                    pl: 2,
                                                    py: 1
                                                }}
                                            >
                                                {/* Add your use cases menu items here */}
                                                <Button
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        px: 2,
                                                        py: 1,
                                                        textAlign: 'left',
                                                        width: '100%',
                                                        color: 'text.secondary',
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover',
                                                            color: 'text.primary'
                                                        }
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        Cross-Border Payments
                                                    </Typography>
                                                </Button>
                                                <Button
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        px: 2,
                                                        py: 1,
                                                        textAlign: 'left',
                                                        width: '100%',
                                                        color: 'text.secondary',
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover',
                                                            color: 'text.primary'
                                                        }
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        Gaming
                                                    </Typography>
                                                </Button>
                                                <Button
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        px: 2,
                                                        py: 1,
                                                        textAlign: 'left',
                                                        width: '100%',
                                                        color: 'text.secondary',
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover',
                                                            color: 'text.primary'
                                                        }
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        Wallets and Exchanges
                                                    </Typography>
                                                </Button>

                                                <Button
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                        px: 2,
                                                        py: 1,
                                                        textAlign: 'left',
                                                        width: '100%',
                                                        color: 'text.secondary',
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover',
                                                            color: 'text.primary'
                                                        }
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        Others
                                                    </Typography>
                                                </Button>
                                            </Stack>
                                        </Collapse>
                                    </Box>
                                );
                            }

                            // Handle other menu items
                            const linkProps = item.external
                                ? { href: item.path, target: '_blank', rel: 'noopener noreferrer' }
                                : { href: item.path };

                            return (
                                <Button
                                    component="a"
                                    key={item.title}
                                    {...linkProps}
                                    onClick={(e) => {
                                        if (item.title === 'Products') {
                                            e.preventDefault();
                                            const element = document.getElementById('products-section');
                                            if (element) {
                                                element.scrollIntoView({ behavior: 'smooth' });
                                            }
                                            onClose?.();
                                        }
                                    }}
                                    sx={{
                                        justifyContent: 'flex-start',
                                        px: 2,
                                        py: 1,
                                        textAlign: 'left',
                                        width: '100%',
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                >
                                    <Typography variant="subtitle2">
                                        {item.title}
                                    </Typography>
                                </Button>
                            );
                        })}
                        {/* <Button
                            component="a"
                            href="https://cal.com/support-team-qsszsx"
                            target="_blank"
                            variant="contained"
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                fontWeight: 600,
                                mt: 2
                            }}
                        >
                            Talk to us
                        </Button> */}
                    </Stack>
                </Box>
            </Box>
        </Drawer>
    );
}; 